import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Spinner,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
  Row,
  Col,
  Input,
  InputGroup,
  Card,
  CardBody,
  CardHeader,
  Container
} from 'reactstrap';
import moment from 'moment';
import api from '../../../../Api';
import { SharedSlidingPane } from '../../../../components/SharedSlidingPage';
import { connect } from 'react-redux';

const ReportClassroom = React.memo(({ classroom, isOpen, onClose, translate }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedStudents, setExpandedStudents] = useState({});

  const [sortOption, setSortOption] = useState('none');

  const [expandedTask, setExpandedTask] = useState(null);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskSubmissionsPage, setTaskSubmissionsPage] = useState(1);
  const submissionsPerPage = 10;

  const [submissionSortOption, setSubmissionSortOption] = useState('none');

  const [goToPage, setGoToPage] = useState('');
  const [goToTaskPage, setGoToTaskPage] = useState('');

  useEffect(() => {
    api.getClassroomReport(classroom)
      .then(res => {
        setReport(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Não foi possível carregar o relatório.');
        setLoading(false);
      });
  }, [classroom]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => onClose(), 500);
  }, [onClose]);

  const toggleExpand = useCallback((studentId) => {
    setExpandedStudents(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  }, []);

  const showTaskDetails = useCallback((taskId) => {
    setExpandedTask(prev => (prev === taskId ? null : taskId));
    setTaskSearchTerm('');
    setTaskSubmissionsPage(1);
    setSubmissionSortOption('none');
  }, []);

  const changePage = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    setGoToPage('');
  }, []);

  const changeTaskPage = useCallback((pageNumber) => {
    setTaskSubmissionsPage(pageNumber);
    setGoToTaskPage('');
  }, []);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleTaskSearch = useCallback((e) => {
    setTaskSearchTerm(e.target.value);
    setTaskSubmissionsPage(1);
  }, []);

  const filteredStudents = useMemo(() => {
    if (!report) return [];
    return report.students.filter(student => {
      const fullName = `${student.name} ${student.lastName}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase()) ||
             student.email.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [report, searchTerm]);

  const sortedStudents = useMemo(() => {
    if (!report) return [];
    const arr = [...filteredStudents];

    const hasDelivered = (studentId) => {
      return report.submissions.some(sub => sub.userId === studentId);
    };

    if (sortOption === 'deliveredFirst') {
      arr.sort((a, b) => {
        const aDel = hasDelivered(a.id);
        const bDel = hasDelivered(b.id);
        return (aDel === bDel) ? 0 : (aDel ? -1 : 1);
      });
    } else if (sortOption === 'notDeliveredFirst') {
      arr.sort((a, b) => {
        const aDel = hasDelivered(a.id);
        const bDel = hasDelivered(b.id);
        return (aDel === bDel) ? 0 : (aDel ? 1 : -1);
      });
    }
    return arr;
  }, [filteredStudents, sortOption, report]);


  const totalPages = useMemo(
    () => Math.ceil(sortedStudents.length / studentsPerPage),
    [sortedStudents.length]
  );

  const currentStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * studentsPerPage;
    return sortedStudents.slice(startIndex, startIndex + studentsPerPage);
  }, [sortedStudents, currentPage]);

  const filteredTaskStudents = useMemo(() => {
    if (!report) return [];
    return report.students.filter(student => {
      const fullName = `${student.name} ${student.lastName}`.toLowerCase();
      return fullName.includes(taskSearchTerm.toLowerCase()) ||
             student.email.toLowerCase().includes(taskSearchTerm.toLowerCase());
    });
  }, [report, taskSearchTerm]);

  const totalTaskPages = useMemo(
    () => Math.ceil(filteredTaskStudents.length / submissionsPerPage),
    [filteredTaskStudents.length]
  );

  const currentTaskStudents = useMemo(() => {
    const startIndex = (taskSubmissionsPage - 1) * submissionsPerPage;
    return filteredTaskStudents.slice(startIndex, startIndex + submissionsPerPage);
  }, [filteredTaskStudents, taskSubmissionsPage]);

  const sortedCurrentTaskStudents = useMemo(() => {
    if (!report || expandedTask === null) return currentTaskStudents;
    const arr = [...currentTaskStudents];

    const hasSubmittedThisTask = (studentId) =>
      report.submissions.some(s => s.userId === studentId && s.taskId === expandedTask);

    const hasGrade = (studentId) => {
      const sub = report.submissions.find(s => s.userId === studentId && s.taskId === expandedTask);
      return sub?.score != null;
    };

    if (submissionSortOption === 'deliveredFirst') {
      arr.sort((a, b) => {
        const aSub = hasSubmittedThisTask(a.id);
        const bSub = hasSubmittedThisTask(b.id);
        return (aSub === bSub) ? 0 : (aSub ? -1 : 1);
      });
    } else if (submissionSortOption === 'notDeliveredFirst') {
      arr.sort((a, b) => {
        const aSub = hasSubmittedThisTask(a.id);
        const bSub = hasSubmittedThisTask(b.id);
        return (aSub === bSub) ? 0 : (aSub ? 1 : -1);
      });
    } else if (submissionSortOption === 'gradedFirst') {
      arr.sort((a, b) => {
        const aHas = hasGrade(a.id);
        const bHas = hasGrade(b.id);
        return (aHas === bHas) ? 0 : (aHas ? -1 : 1);
      });
    }
    return arr;
  }, [currentTaskStudents, submissionSortOption, report, expandedTask]);

  return (
    <SharedSlidingPane
      isOpen={isOpen && !isClosing}
      title={translate['relatorio_da_turma'] || "Relatório da Turma"}
      width="70%"
      onClose={handleClose}
    >
      <Container fluid className="p-3">
        {loading && (
          <div className="text-center py-5">
            <Spinner size="lg" />
          </div>
        )}

        {error && (
          <div className="text-danger text-center">
            {translate['nao_foi_possivel_carregar_o_relatorio'] || error}
          </div>
        )}

        {!loading && !error && report && (
          <>
            <Card className="mb-4 shadow-sm">
              <CardHeader tag="h5">
                {translate['tarefas_disponiveis'] || "Tarefas Disponíveis"}
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table size="sm" striped bordered>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '80px' }}>ID</th>
                        <th>{translate['nome'] || "Nome"}</th>
                        <th style={{ width: '150px' }}>{translate['acoes'] || "Ações"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.tasks.map(task => (
                        <tr key={task.id}>
                          <td>{task.id}</td>
                          <td>{task.name}</td>
                          <td>
                            <Button
                              color={expandedTask === task.id ? 'primary' : 'secondary'}
                              size="sm"
                              onClick={() => showTaskDetails(task.id)}
                            >
                              {expandedTask === task.id
                                ? translate['ocultar'] || 'Ocultar'
                                : translate['ver_entregas'] || 'Ver Entregas'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {report.tasks.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-center">
                            {translate['nenhuma_tarefa_disponivel'] || "Nenhuma tarefa disponível"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>

            {expandedTask && (
              <Card className="mb-4 shadow-sm">
                <CardHeader tag="h5">
                  {translate['entregas_da_tarefa'] || "Entregas da Tarefa"}: {report.tasks.find(t => t.id === expandedTask)?.name}
                </CardHeader>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={6}>
                      <InputGroup>
                        <Input
                          placeholder={translate['buscar_aluno'] || "Buscar aluno..."}
                          value={taskSearchTerm}
                          onChange={handleTaskSearch}
                          size="sm"
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3} className="ml-auto">
                      <Input
                        type="select"
                        value={submissionSortOption}
                        onChange={e => {
                          setSubmissionSortOption(e.target.value);
                          setTaskSubmissionsPage(1);
                        }}
                        bsSize="sm"
                        className="form-control-sm"
                      >
                        <option value="none">{translate['ordenar_entregas'] || "Ordenar entregas"}</option>
                        <option value="deliveredFirst">{translate['entregues_primeiro'] || "Entregues primeiro"}</option>
                        <option value="notDeliveredFirst">{translate['nao_entregues_primeiro'] || "Não entregues primeiro"}</option>
                        <option value="gradedFirst">{translate['com_nota_primeiro'] || "Com nota primeiro"}</option>
                      </Input>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table size="sm" striped bordered>
                      <thead className="thead-light">
                        <tr>
                          <th>{translate['nome_completo'] || "Nome Completo"}</th>
                          <th>{translate['email'] || "Email"}</th>
                          <th>{translate['nota'] || "Nota"}</th>
                          <th>{translate['data_de_entrega'] || "Data de Entrega"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCurrentTaskStudents.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center">
                              {taskSearchTerm
                                ? translate['nenhum_aluno_encontrado_com_termo'] || 'Nenhum aluno encontrado com esse termo.'
                                : translate['nao_ha_alunos_nesta_turma'] || 'Não há alunos nesta turma.'}
                            </td>
                          </tr>
                        ) : (
                          sortedCurrentTaskStudents.map(student => {
                            const submission = report.submissions.find(
                              s => s.userId === student.id && s.taskId === expandedTask
                            );
                            return (
                              <tr key={student.id}>
                                <td>{`${student.name} ${student.lastName}`}</td>
                                <td>{student.email}</td>
                                <td>{submission?.score ?? '-'}</td>
                                <td>
                                  {submission?.submittedAt
                                    ? moment(submission.submittedAt).format('DD/MM/YYYY HH:mm')
                                    : translate['nao_entregue'] || 'Não entregue'}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {totalTaskPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
                      <Pagination size="sm">
                        <PaginationItem disabled={taskSubmissionsPage === 1}>
                          <PaginationLink first onClick={() => changeTaskPage(1)} />
                        </PaginationItem>
                        <PaginationItem disabled={taskSubmissionsPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => changeTaskPage(taskSubmissionsPage - 1)}
                          />
                        </PaginationItem>

                        {taskSubmissionsPage > 3 && (
                          <PaginationItem>
                            <PaginationLink onClick={() => changeTaskPage(1)}>1</PaginationLink>
                          </PaginationItem>
                        )}

                        {taskSubmissionsPage > 4 && (
                          <PaginationItem disabled>
                            <PaginationLink tag="span">...</PaginationLink>
                          </PaginationItem>
                        )}

                        {[...Array(totalTaskPages)].map((_, i) => {
                          const pageNum = i + 1;
                          if (
                            pageNum >= taskSubmissionsPage - 1 &&
                            pageNum <= taskSubmissionsPage + 1 &&
                            pageNum <= totalTaskPages
                          ) {
                            return (
                              <PaginationItem
                                key={pageNum}
                                active={pageNum === taskSubmissionsPage}
                              >
                                <PaginationLink onClick={() => changeTaskPage(pageNum)}>
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          }
                          return null;
                        })}

                        {taskSubmissionsPage < totalTaskPages - 3 && (
                          <PaginationItem disabled>
                            <PaginationLink tag="span">...</PaginationLink>
                          </PaginationItem>
                        )}

                        {taskSubmissionsPage < totalTaskPages - 2 && (
                          <PaginationItem>
                            <PaginationLink onClick={() => changeTaskPage(totalTaskPages)}>
                              {totalTaskPages}
                            </PaginationLink>
                          </PaginationItem>
                        )}

                        <PaginationItem disabled={taskSubmissionsPage === totalTaskPages}>
                          <PaginationLink
                            next
                            onClick={() => changeTaskPage(taskSubmissionsPage + 1)}
                          />
                        </PaginationItem>
                        <PaginationItem disabled={taskSubmissionsPage === totalTaskPages}>
                          <PaginationLink last onClick={() => changeTaskPage(totalTaskPages)} />
                        </PaginationItem>
                      </Pagination>

                      {totalTaskPages > 5 && (
                        <div className="d-flex align-items-center">
                          <span className="mr-2 text-muted small">{translate['ir_para_pagina'] || "Ir para página:"}</span>
                          <Input
                            type="number"
                            min="1"
                            max={totalTaskPages}
                            value={goToTaskPage}
                            onChange={e => setGoToTaskPage(e.target.value)}
                            className="text-center form-control-sm"
                            style={{
                              width: '60px',
                              height: '30px',
                              padding: '0.25rem',
                              marginRight: '0.5rem'
                            }}
                            onKeyPress={e => {
                              if (e.key === 'Enter') {
                                const pageNum = parseInt(goToTaskPage, 10);
                                if (pageNum && pageNum > 0 && pageNum <= totalTaskPages) {
                                  changeTaskPage(pageNum);
                                  setGoToTaskPage('');
                                }
                              }
                            }}
                          />
                          <Button
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={() => {
                              const pageNum = parseInt(goToTaskPage, 10);
                              if (pageNum && pageNum > 0 && pageNum <= totalTaskPages) {
                                changeTaskPage(pageNum);
                                setGoToTaskPage('');
                              }
                            }}
                          >
                            {translate['ir'] || "Ir"}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            <Card className="mb-4 shadow-sm">
              <CardBody>
                
              </CardBody>
            </Card>

            <Card className="shadow-sm">
              <CardHeader tag="h5">{translate['desempenho_dos_alunos'] || "Desempenho dos Alunos"}</CardHeader>
              
              <CardBody>
                <Row className="align-items-center mb-3">
                  <Col md={3} className="mb-2 mb-md-0">
                  <InputGroup>
                      <Input
                        placeholder={translate['buscar_aluno'] || "Buscar aluno..."}
                        value={searchTerm}
                        onChange={handleSearch}
                        bsSize="sm"
                      />
                    </InputGroup>
                  </Col>
                  
                  <Col md={4} className="mb-md-0">
                    <Input
                      type="select"
                      value={sortOption}
                      onChange={e => {
                        setSortOption(e.target.value);
                        setCurrentPage(1);
                      }}
                      bsSize="sm"
                      className="form-control-sm"
                    >
                      <option value="none">{translate['ordenar_alunos'] || "Ordenar alunos"}</option>
                      <option value="deliveredFirst">{translate['entregues_primeiro'] || "Entregues primeiro"}</option>
                      <option value="notDeliveredFirst">{translate['nao_entregues_primeiro'] || "Não entregues primeiro"}</option>
                    </Input>
                  </Col>
                  <Col md={4} className="text-md-right text-muted small">
                    {translate['mostrando'] || "Mostrando"} {filteredStudents.length} {translate['de'] || "de"} {report.students.length} {translate['alunos'] || "alunos"}
                  </Col>
                </Row>
                <div className="table-responsive">
                  <Table striped bordered>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '40%' }}>{translate['nome_completo'] || "Nome Completo"}</th>
                        <th style={{ width: '30%' }}>{translate['email'] || "Email"}</th>
                        <th style={{ width: '15%' }}>{translate['entregas'] || "Entregas"}</th>
                        <th style={{ width: '15%' }}>{translate['acoes'] || "Ações"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentStudents.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            {searchTerm
                              ? translate['nenhum_aluno_encontrado_com_termo'] || 'Nenhum aluno encontrado com esse termo.'
                              : translate['nao_ha_alunos_nesta_turma'] || 'Não há alunos nesta turma.'}
                          </td>
                        </tr>
                      ) : (
                        currentStudents.map(student => {
                          const studentSubmissions = report.submissions.filter(
                            s => s.userId === student.id
                          );
                          const completedCount = studentSubmissions.length;
                          const withGradeCount = studentSubmissions.filter(
                            s => s.score !== null
                          ).length;

                          return (
                            <React.Fragment key={student.id}>
                              <tr>
                                <td>{`${student.name} ${student.lastName}`}</td>
                                <td>{student.email}</td>
                                <td>
                                  {completedCount}/{report.tasks.length} {translate['entregas'] || "entregas"}
                                  <br />
                                  {withGradeCount} {translate['avaliadas'] || "avaliadas"}
                                </td>
                                <td>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => toggleExpand(student.id)}
                                  >
                                    {expandedStudents[student.id]
                                      ? translate['ocultar'] || 'Ocultar'
                                      : translate['detalhes'] || 'Detalhes'}
                                  </Button>
                                </td>
                              </tr>

                              {expandedStudents[student.id] && (
                                <tr>
                                  <td colSpan="4" className="p-0">
                                    <div
                                      className="p-3 bg-white"
                                      style={{ border: '1px solid #dee2e6' }}
                                    >
                                      <h6 className="mb-3">
                                        {translate['entregas_de'] || "Entregas de"} {`${student.name} ${student.lastName}`}
                                      </h6>
                                      <Table size="sm" bordered>
                                        <thead className="thead-light">
                                          <tr>
                                            <th>{translate['tarefa'] || "Tarefa"}</th>
                                            <th>{translate['nota'] || "Nota"}</th>
                                            <th>{translate['data_de_entrega'] || "Data de Entrega"}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {report.tasks.map(task => {
                                            const submission = report.submissions.find(
                                              s =>
                                                s.userId === student.id &&
                                                s.taskId === task.id
                                            );
                                            return (
                                              <tr key={task.id}>
                                                <td>{task.name}</td>
                                                <td>{submission?.score ?? '-'}</td>
                                                <td>
                                                  {submission?.submittedAt
                                                    ? moment(submission.submittedAt).format(
                                                        'DD/MM/YYYY HH:mm'
                                                      )
                                                    : translate['nao_entregue'] || 'Não entregue'}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
                    <Pagination size="sm">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink first onClick={() => changePage(1)} />
                      </PaginationItem>
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          previous
                          onClick={() => changePage(currentPage - 1)}
                        />
                      </PaginationItem>

                      {currentPage > 3 && (
                        <PaginationItem>
                          <PaginationLink onClick={() => changePage(1)}>1</PaginationLink>
                        </PaginationItem>
                      )}

                      {currentPage > 4 && (
                        <PaginationItem disabled>
                          <PaginationLink tag="span">...</PaginationLink>
                        </PaginationItem>
                      )}

                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        if (
                          pageNum >= currentPage - 1 &&
                          pageNum <= currentPage + 1 &&
                          pageNum <= totalPages
                        ) {
                          return (
                            <PaginationItem key={pageNum} active={pageNum === currentPage}>
                              <PaginationLink onClick={() => changePage(pageNum)}>
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}

                      {currentPage < totalPages - 3 && (
                        <PaginationItem disabled>
                          <PaginationLink tag="span">...</PaginationLink>
                        </PaginationItem>
                      )}

                      {currentPage < totalPages - 2 && (
                        <PaginationItem>
                          <PaginationLink onClick={() => changePage(totalPages)}>
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      )}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink next onClick={() => changePage(currentPage + 1)} />
                      </PaginationItem>
                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink last onClick={() => changePage(totalPages)} />
                      </PaginationItem>
                    </Pagination>

                    {totalPages > 5 && (
                      <div className="d-flex align-items-center">
                        <span className="mr-2 text-muted small">{translate['ir_para_pagina'] || "Ir para página:"}</span>
                        <Input
                          type="number"
                          min="1"
                          max={totalPages}
                          value={goToPage}
                          onChange={e => setGoToPage(e.target.value)}
                          className="text-center form-control-sm"
                          style={{ width: '60px', height: '30px', padding: '0.25rem', marginRight: '0.5rem' }}
                          onKeyPress={e => {
                            if (e.key === 'Enter') {
                              const pageNum = parseInt(goToPage, 10);
                              if (pageNum && pageNum > 0 && pageNum <= totalPages) {
                                changePage(pageNum);
                                setGoToPage('');
                              }
                            }
                          }}
                        />
                        <Button
                          color="link"
                          size="sm"
                          className="p-0"
                          onClick={() => {
                            const pageNum = parseInt(goToPage, 10);
                            if (pageNum && pageNum > 0 && pageNum <= totalPages) {
                              changePage(pageNum);
                              setGoToPage('');
                            }
                          }}
                        >
                          {translate['ir'] || "Ir"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-center text-muted small mt-2">
                  {translate['mostrando'] || "Mostrando"}{' '}
                  {sortedStudents.length > 0
                    ? (currentPage - 1) * studentsPerPage + 1
                    : 0}{' '}
                  {translate['a'] || "a"}{' '}
                  {Math.min(currentPage * studentsPerPage, sortedStudents.length)}{' '}
                  {translate['de'] || "de"} {sortedStudents.length} {translate['alunos'] || "alunos"}
                </div>
                <Card className="mb-4 shadow-sm">
              <CardBody>
                <Row>
                  <Col md={6} className="mb-2 mb-md-0">
                    
                  </Col>
                  <Col md={6} className="d-flex justify-content-end align-items-center">
                    <div className="text-muted small">
                      {translate['total'] || "Total"}: {report.students.length} {translate['alunos'] || "alunos"}, {report.tasks.length} {translate['tarefas'] || "tarefas"}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
              </CardBody>
            </Card>
          </>
        )}
      </Container>
    </SharedSlidingPane>
  );
});

export default connect( state => ({
    translate: state.translate
}))(ReportClassroom);